import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DropdownDefaultItem,
  DropdownOption,
  DropdownsWithUnassigned,
  GroupedDropdownOption,
} from '../data-types/dropdown-option';
import { EntityType } from '../data-types/entity-types';

export function observableToDropdownOptions<T>(
  obs$: Observable<readonly T[]>,
  id = 'id',
  value = 'name',
): DropdownsWithUnassigned<DropdownOption[]> {
  return obs$.pipe(
    map((options) => {
      return options.map((option) => {
        return {
          text: option[value],
          value: option[id],
        };
      });
    }),
  );
}

/**
 * Used for hard-coded string literals
 */
export function recordToDropdownOptions<K extends string | number>(
  literals: Record<K, string>,
): Observable<DropdownOption[]> {
  return of(
    Object.entries(literals).map(([key, text]: [string, string]) => ({
      text,
      value: key !== '' && !Number.isNaN(Number(key)) ? Number(key) : (key as unknown as string),
    })),
  );
}

export function recordToGroupedDropdownOptions(
  literals: Record<string | number, { label: string; category: string }>,
): Observable<GroupedDropdownOption[]> {
  const options: GroupedDropdownOption[] = Object.entries(literals).map(([key, v]) => ({
    text: v.label,
    value: key !== '' && !Number.isNaN(Number(key)) ? Number(key) : (key as unknown as string),
    category: v.category,
  }));

  return of(options);
}

export function entityToDropdownOption<T extends EntityType>(entity: T): DropdownOption | null {
  if (!entity) {
    return null;
  }

  return {
    text: entity.name,
    value: +entity.id!,
  };
}

export function dropdownOptionToEntity(option: DropdownOption): EntityType | null {
  if (!option) {
    return null;
  }

  return {
    id: option.value,
    name: option.text,
  };
}

export const isDropdownValue = (value: unknown) => typeof value === 'object' && 'value' in value!;

export function addDefaultOption() {
  return (source: Observable<DropdownOption[]>): Observable<DropdownOption[]> =>
    source.pipe(map((items) => [DropdownDefaultItem, ...(items ?? [])]));
}
